import * as React from 'react';
import { Info, Loader2, Pencil, Plus, Star, Trash2 } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import avatarPlaceholder from 'assets/icons/Avatar.svg';
import { PaginationBar } from 'core/components/PaginationBar';
import { DigitalMarketingBreadcrumb } from 'features/DigitalMarketing/components/DigitalMarketingBreadcrumb';
import { DigitalMarketingDeleteModal } from 'features/DigitalMarketing/components/DigitalMarketingDeleteModal';
import {
  buildEditWebsiteContentPath,
  EDIT_WEBSITE_CONTENT_BASE_PATH,
} from 'features/DigitalMarketing/routes/editWebsiteContentPaths';
import { Testimonial } from 'features/DigitalMarketing/pages/EditWebsiteContent/api/testimonialsApi';
import { useTestimonials } from 'features/DigitalMarketing/pages/EditWebsiteContent/hooks/useTestimonials';
import {
  DMButton,
  DMDiv,
  DMImage,
  DMText,
  DMTextarea,
  DMStyle,
} from '../../../components/Primitives';

const TestimonialStars = ({ rating }: { rating: number }) => (
  <DMDiv
    className="mt-[2.5vh] flex items-center gap-[0.42vw]"
    aria-label={`${rating} star rating`}
  >
    {Array.from({ length: 5 }).map((_, index) => (
      <Star
        key={index}
        className={`h-[2.45vh] w-[2.45vh] ${
          index < Math.round(rating)
            ? 'fill-[#FF7A1A] text-[#FF7A1A]'
            : 'fill-gray-200 text-gray-200'
        }`}
        strokeWidth={1.8}
      />
    ))}
  </DMDiv>
);

const TestimonialCard = ({
  testimonial,
  onDelete,
  onEdit,
  onPublish,
  isUpdatingStatus,
}: {
  testimonial: Testimonial;
  onDelete: (id: number) => void;
  onEdit: (testimonial: Testimonial) => void;
  onPublish: (id: number) => void;
  isUpdatingStatus: boolean;
}) => (
  <DMDiv
    as="article"
    className="relative flex min-h-[42.5vh] flex-col overflow-hidden rounded-[18px] bg-white px-[1.95vw] pb-[2.25vh] pt-[5.2vh] shadow-[0_10px_28px_rgba(0,0,0,0.06)]"
  >
    {testimonial.status === 'Live' && (
      <DMText
        as="span"
        className="absolute right-0 top-0 flex h-[5vh] min-w-[9.4vw] items-center justify-center rounded-bl-[4px] bg-[#FFB547] px-[1vw] text-[1.45vh] font-medium leading-none text-gray-700"
      >
        Live on website
      </DMText>
    )}

    <DMDiv className="flex items-start gap-[1.15vw]">
      <DMImage
        src={testimonial.avatar || avatarPlaceholder}
        alt={testimonial.clientName}
        onError={event => {
          event.currentTarget.onerror = null;
          event.currentTarget.src = avatarPlaceholder;
        }}
        className="h-[6.4vh] w-[6.4vh] shrink-0 rounded-full object-cover"
      />
      <DMDiv className="min-w-0 pt-[0.35vh]">
        <DMText
          as="h2"
          title={testimonial.clientName}
          className="truncate text-[2.2vh] font-bold leading-tight text-gray-950"
        >
          {testimonial.clientName}
        </DMText>
        <DMText
          title={testimonial.companyName}
          className="mt-[0.15vh] truncate text-[1.75vh] font-medium leading-tight text-gray-500"
        >
          {testimonial.companyName}
        </DMText>
      </DMDiv>
    </DMDiv>

    <TestimonialStars rating={testimonial.rating} />

    <DMTextarea
      title={testimonial.feedback}
      value={testimonial.feedback}
      readOnly
      aria-label="Testimonial description"
      className="testimonial-description-scroll mt-[1.4vh] h-[15vh] resize-none overflow-y-auto rounded-[8px] border-0 bg-transparent p-0 text-[2vh] font-semibold leading-[1.72] text-gray-950 outline-none [overflow-wrap:anywhere]"
    />

    <DMText className="mt-auto pt-[1.2vh] self-end pr-[0.2vw] text-[1.65vh] font-medium leading-none text-gray-500">
      {testimonial.date}
    </DMText>

    <DMDiv className="mt-[2.2vh] flex items-center gap-[1vw] border-t border-gray-100 pt-[2vh]">
      <DMButton
        type="button"
        onClick={() => onDelete(testimonial.id)}
        aria-label="Delete testimonial"
        className="flex h-[4.2vh] w-[4.2vh] shrink-0 items-center justify-center rounded-full bg-gray-50 text-red-500 transition-colors hover:bg-red-50"
      >
        <Trash2 className="h-[2vh] w-[2vh]" strokeWidth={2} />
      </DMButton>
      <DMButton
        type="button"
        onClick={() => onEdit(testimonial)}
        aria-label="Edit testimonial"
        className="flex h-[4.2vh] w-[4.2vh] shrink-0 items-center justify-center rounded-full bg-gray-50 text-gray-500 transition-colors hover:bg-gray-100"
      >
        <Pencil className="h-[2vh] w-[2vh]" strokeWidth={1.9} />
      </DMButton>
      <DMButton
        type="button"
        onClick={() => onPublish(testimonial.id)}
        disabled={isUpdatingStatus || testimonial.status === 'Live'}
        className="flex h-[4.2vh] flex-1 items-center justify-center gap-[0.5vw] rounded-full bg-primary-main px-[1.4vw] text-[1.45vh] font-medium leading-none text-white transition-opacity hover:opacity-90 disabled:cursor-not-allowed disabled:opacity-60"
      >
        {isUpdatingStatus && (
          <Loader2
            className="h-[1.7vh] w-[1.7vh] animate-spin"
            strokeWidth={2}
          />
        )}
        <DMText as="span">
          {testimonial.status === 'Live' ? 'Published' : 'Publish'}
        </DMText>
      </DMButton>
    </DMDiv>
  </DMDiv>
);

export function Testimonials() {
  const navigate = useNavigate();
  const [testimonialIdToDelete, setTestimonialIdToDelete] = React.useState<
    number | null
  >(null);
  const {
    testimonials,
    pageIndex,
    pageSize,
    totalRecords,
    isLoadingTestimonials,
    isDeletingTestimonial,
    publishingTestimonialId,
    setPageIndex,
    setPageSize,
    deleteTestimonial,
    publishTestimonial,
  } = useTestimonials();

  const handleDelete = (id: number) => {
    setTestimonialIdToDelete(id);
  };

  const handleCancelDelete = () => {
    if (isDeletingTestimonial) {
      return;
    }

    setTestimonialIdToDelete(null);
  };

  const handleConfirmDelete = async () => {
    if (!testimonialIdToDelete) {
      return;
    }

    const wasDeleted = await deleteTestimonial(testimonialIdToDelete);
    if (wasDeleted) {
      setTestimonialIdToDelete(null);
    }
  };

  const handleEdit = (testimonial: Testimonial) => {
    navigate(buildEditWebsiteContentPath('testimonials/edit'), {
      state: { testimonial },
    });
  };

  const handleAdd = () => {
    navigate(buildEditWebsiteContentPath('testimonials/create'));
  };

  return (
    <DMDiv
      as="main"
      className="flex h-full min-h-0 flex-col overflow-hidden bg-white px-[2.5vw] py-[3.2vh] font-inter text-gray-900"
    >
      <DMDiv className="mb-[2.1vh] flex shrink-0 items-start justify-between gap-[2vw]">
        <DMDiv className="min-w-0">
          <DigitalMarketingBreadcrumb
            className="mb-[1.15vh]"
            items={[
              {
                label: 'Edit Website Content',
                path: EDIT_WEBSITE_CONTENT_BASE_PATH,
              },
              { label: 'Testimonials' },
            ]}
          />
          <DMText
            as="h1"
            className="text-[3.9vh] font-bold leading-tight text-gray-950"
          >
            Testimonials
          </DMText>
          <DMText className="mt-[0.6vh] text-[1.85vh] font-medium leading-tight text-gray-600">
            AI collects professional testimonials with Name, Relation, Feedback,
            and Rating.
          </DMText>
        </DMDiv>

        <DMButton
          type="button"
          onClick={handleAdd}
          className="mt-[1vh] flex h-[6.7vh] items-center justify-center gap-[0.9vw] rounded-full bg-primary-main px-[2.3vw] text-[1.75vh] font-semibold leading-none text-white shadow-sm transition-opacity hover:opacity-90"
        >
          <Plus className="h-[2.5vh] w-[2.5vh]" strokeWidth={2.2} />
          <DMText as="span">Add Testimonial</DMText>
        </DMButton>
      </DMDiv>

      <DMDiv className="mb-[1.4vh] flex shrink-0 items-start gap-[0.9vw] rounded-[8px] border border-sky-400 bg-sky-50 px-[1.25vw] py-[2vh] text-sky-600">
        <Info
          className="mt-[0.1vh] h-[2.3vh] w-[2.3vh] shrink-0"
          strokeWidth={2.1}
        />
        <DMDiv>
          <DMText className="text-[1.6vh] font-bold leading-tight">
            Message
          </DMText>
          <DMText className="mt-[0.55vh] text-[1.55vh] font-medium leading-tight">
            Upload up to 10 professional testimonials from your existing
            feedback, client responses, or reviews to showcase on your website.
          </DMText>
        </DMDiv>
      </DMDiv>

      <DMDiv
        as="section"
        className="flex min-h-0 flex-1 flex-col rounded-[18px] border border-gray-300 bg-gray-50 px-[1.5vw] py-[2.4vh]"
      >
        <DMDiv className="grid min-h-0 flex-1 grid-cols-3 content-start gap-x-[1.65vw] gap-y-[2vh] overflow-y-auto">
          {testimonials.map(testimonial => (
            <TestimonialCard
              key={testimonial.id}
              testimonial={testimonial}
              onDelete={handleDelete}
              onEdit={handleEdit}
              onPublish={publishTestimonial}
              isUpdatingStatus={publishingTestimonialId === testimonial.id}
            />
          ))}
          {!isLoadingTestimonials && testimonials.length === 0 && (
            <DMDiv className="col-span-3 flex h-[34vh] items-center justify-center rounded-[8px] border border-dashed border-gray-300 bg-white text-[1.8vh] font-semibold text-gray-500">
              No testimonials found
            </DMDiv>
          )}
          {isLoadingTestimonials &&
            Array.from({ length: 6 }).map((_, index) => (
              <DMDiv
                key={index}
                className="min-h-[42.5vh] animate-pulse rounded-[18px] bg-white px-[1.95vw] pb-[2.25vh] pt-[3.9vh] shadow-[0_10px_28px_rgba(0,0,0,0.06)]"
              >
                <DMDiv className="flex items-center gap-[1.15vw]">
                  <DMDiv className="h-[6.4vh] w-[6.4vh] rounded-full bg-gray-200" />
                  <DMDiv className="space-y-[0.9vh]">
                    <DMDiv className="h-[1.9vh] w-[8vw] rounded bg-gray-200" />
                    <DMDiv className="h-[1.5vh] w-[5vw] rounded bg-gray-200" />
                  </DMDiv>
                </DMDiv>
                <DMDiv className="mt-[3vh] h-[2vh] w-[10vw] rounded bg-gray-200" />
                <DMDiv className="mt-[2.5vh] space-y-[1.2vh]">
                  <DMDiv className="h-[1.8vh] w-full rounded bg-gray-200" />
                  <DMDiv className="h-[1.8vh] w-[85%] rounded bg-gray-200" />
                  <DMDiv className="h-[1.8vh] w-[70%] rounded bg-gray-200" />
                </DMDiv>
              </DMDiv>
            ))}
        </DMDiv>

        {(isLoadingTestimonials || totalRecords > 0) && (
          <DMDiv className="mt-[2vh] shrink-0">
            <PaginationBar
              key={`testimonials-${pageSize}`}
              pageIndex={pageIndex}
              pageSize={pageSize}
              totalRecords={totalRecords}
              onPageChange={setPageIndex}
              onPageSizeChange={setPageSize}
              itemName="testimonials"
            />
          </DMDiv>
        )}
      </DMDiv>

      <DigitalMarketingDeleteModal
        isOpen={testimonialIdToDelete !== null}
        title="Are you sure you want to delete this testimonial?"
        isDeleting={isDeletingTestimonial}
        onCancel={handleCancelDelete}
        onConfirm={handleConfirmDelete}
      />

      <DMStyle>
        {`
                    .testimonial-description-scroll {
                        scrollbar-width: thin;
                        scrollbar-color: #d1d5db transparent;
                    }

                    textarea.testimonial-description-scroll::-webkit-scrollbar {
                        width: 1px !important;
                        height: 1px !important;
                    }

                    textarea.testimonial-description-scroll::-webkit-scrollbar-thumb {
                        border-radius: 999px;
                        background: #d1d5db !important;
                    }

                    textarea.testimonial-description-scroll::-webkit-scrollbar-track {
                        background: transparent !important;
                    }
                `}
      </DMStyle>
    </DMDiv>
  );
}
